import React, { useMemo } from "react";
import Grid from "@mui/material/Grid";
import ReminderIcon from "../../../assets/icons/contract/toggleable/IconTime";
import DebtCollectionIcon from "../../../assets/icons/contract/toggleable/IconCompany";
import PaymentOrderIcon from "../../../assets/icons/provision/toggleable/IconPostbox";
import LawsuitIcon from "../../../assets/icons/generic/toggleable/IconCourtDetailed";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import { translate } from "../../../services/translations/translations";
import _includes from "lodash/includes";
import Person from "../Common/Person/Person";
import Address from "../Common/Address/Address";
import MoneyIcon from "../../../assets/icons/contract/toggleable/IconMoneyEnforce";
import GoodsIcon from "../../../assets/icons/contract/toggleable/IconGoodsEnforce";
import { dateFieldDefault, emailDefault, isMoney, isPhoneNumberDefault, requiredValidator } from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Group from "../../Wizard/Group";
import moment from "moment";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import MoneyAndGoodsIcon from "../../../assets/icons/contract/toggleable/IconMoneyAndGoodsEnforce";

export default function ContractBaseDefendClaim() {
  const { values } = useForm();
  const exampleDate = useMemo(() => moment().subtract(1, "weeks").format("DD.MM.YYYY"), []);

  return (
    <>
      <Grid item xs={12}>
        <Group
          type="radio"
          name="defenseProcessStatus"
          question={translate(`rentalContract.defenseProcessStatus.question`, values)}
          options={[
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.none.label`, values),
              labelIcon: <CrossIcon />,
              labelIconActive: <CrossIcon active />,
              value: "none",
            },
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.reminder.label`, values),
              labelIcon: <ReminderIcon />,
              labelIconActive: <ReminderIcon active />,
              value: "reminder",
            },
            ...(values.contractPartyType === "tenant"
              ? [
                  {
                    labelText: translate(`rentalContract.defenseProcessStatus.options.debtCollection.label`, values),
                    labelIcon: <DebtCollectionIcon />,
                    labelIconActive: <DebtCollectionIcon active />,
                    value: "debtCollection",
                  },
                ]
              : []),
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.paymentOrder.label`, values),
              labelIcon: <PaymentOrderIcon />,
              labelIconActive: <PaymentOrderIcon active />,
              optionHintText: "Ein Mahnbescheid kommt immer in einem gelben Brief per Post.",
              value: "paymentOrder",
            },
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.enforcementOrder.label`, values),
              labelIcon: <PaymentOrderIcon />,
              labelIconActive: <PaymentOrderIcon active />,
              optionHintText: "Ein Vollstreckungsbescheid kommt immer in einem gelben Brief per Post.",
              value: "enforcementOrder",
            },
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.lawsuit.label`, values),
              labelIcon: <LawsuitIcon />,
              labelIconActive: <LawsuitIcon active />,
              optionHintText: "Sie haben wegen der Forderung gegen Sie ein Klageschreiben vom Gericht zugestellt bekommen.",
              value: "lawsuit",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      {values.defenseProcessStatus === "paymentOrder" && (
        <>
          <Grid item xs={12}>
            <FormLegend>Wann haben Sie den Mahnbescheid erhalten?</FormLegend>
          </Grid>
          <Grid item xs={12}>
            <ValidatorDateField
              name="paymentOrderDate"
              label="Zustellung Mahnbescheid am"
              helperText={`Das Zustellungsdatum ist auf dem gelben Briefumschlag vermerkt. Bitte geben Sie unbedingt dieses Datum hier an. (z.B. ${exampleDate})`}
              validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault]}
            />
          </Grid>
        </>
      )}
      {values.defenseProcessStatus === "enforcementOrder" && (
        <>
          <Grid item xs={12}>
            <FormLegend>Wann haben Sie den Vollstreckungsbescheid erhalten?</FormLegend>
          </Grid>
          <Grid item xs={12}>
            <ValidatorDateField
              name="enforcementOrderDate"
              label="Zustellung Vollstreckungsbescheid am"
              helperText={`Das Zustellungsdatum ist auf dem gelben Briefumschlag vermerkt. Bitte geben Sie unbedingt dieses Datum hier an. (z.B. ${exampleDate})`}
              validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault]}
            />
          </Grid>
        </>
      )}
      {values.defenseProcessStatus === "lawsuit" && (
        <>
          <Grid item xs={12}>
            <FormLegend>Wann haben sie die Klage erhalten?</FormLegend>
          </Grid>
          <Grid item xs={12}>
            <ValidatorDateField
              name="statementOfClaimFiledDate"
              label="Klage erhalten am"
              helperText={`Hier geht es darum, wann Ihnen die Klage zugestellt wurde. (z.B. ${exampleDate})`}
              validators={[requiredValidator("Bitte geben Sie das Datum an"), dateFieldDefault]}
            />
          </Grid>
        </>
      )}
      {_includes(["paymentOrder", "reminder", "lawsuit"], values.defenseProcessStatus) && (
        <>
          <Grid item xs={12}>
            <Group
              type="radio"
              name="contractualPartnerHasLawyer"
              question={translate("rentalContract.contractualPartnerHasLawyer.question", values)}
              options={[
                {
                  labelText: translate("rentalContract.contractualPartnerHasLawyer.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("rentalContract.contractualPartnerHasLawyer.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
            />
          </Grid>
        </>
      )}
      {values.contractualPartnerHasLawyer === "yes" && (
        <>
          <Person fieldsAreRequired={false} path="opponentLawyer" headline="Wie heißt der Anwalt/die Anwaltskanzlei des Vertragspartners?" />
          <Grid item xs={12}>
            <ValidatorTextField name="opponentLawFirm.name" label="Name der Kanzlei" />
          </Grid>
          <Address
            fieldsAreRequired={false}
            path="opponentLawFirm.address"
            headline="Welche Kontaktdaten des Anwalts/der Anwaltskanzlei liegen Ihnen vor? Diese finden Sie zum Beispiel auf dem Schreiben im Briefkopf."
          />
          <Grid item xs={12}>
            <ValidatorTextField name="opponentLawFirm.email" label="E-Mail-Adresse Anwalt" validators={[emailDefault]} />
          </Grid>
          <Grid item xs={12}>
            <ValidatorTextField name="opponentLawFirm.telephone" label="Telefonnummer Anwalt" validators={isPhoneNumberDefault} />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Group
          type="radio"
          name="subjectOfDispute"
          question={translate(`rentalContract.subjectOfDispute.question`, values)}
          options={[
            {
              labelText: translate(`rentalContract.subjectOfDispute.options.money.label`, values),
              labelIcon: <MoneyIcon />,
              labelIconActive: <MoneyIcon active />,
              value: "money",
            },
            {
              labelText: translate(`rentalContract.subjectOfDispute.options.goods.label`, values),
              labelIcon: <GoodsIcon />,
              labelIconActive: <GoodsIcon active />,
              value: "goods",
            },
            {
              labelText: translate("rentalContract.subjectOfDispute.options.moneyAndGoods.label", values),
              labelIcon: <MoneyAndGoodsIcon sx={{ width: 300 }} />,
              labelIconActive: <MoneyAndGoodsIcon active sx={{ width: 300 }} />,
              value: "moneyAndGoods",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      {!!values.defenseProcessStatus && values.subjectOfDispute === "money" && (
        <>
          <Grid item xs={12}>
            <FormLegend>{translate("rentalContract.claimedAmountClient.question", values)}</FormLegend>
          </Grid>
          <Grid item xs={12}>
            <ValidatorTextField
              label={translate("rentalContract.claimedAmountClient.label", values)}
              name="claimedAmountClient"
              validators={[
                {
                  validator: isMoney,
                  message: "Bitte geben Sie den Wert in Euro und Cent ein - zum Beispiel 136,15 oder 85,00",
                },
              ]}
              helperText={translate("rentalContract.claimedAmountClient.helperText", values)}
            />
          </Grid>
        </>
      )}
    </>
  );
}
